/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import { Epic, ofType } from 'redux-observable';
import {
    UserAction,
    UserActionType,
    loginLoading,
    loginSuccess,
    loginError,
    misUserInfoSuccess,
    misReset,
    misAuthDataLoad,
    misNotificationAction,
    misAuthCompleteSuccessAction,
    validateUserAction,
} from '../actions/userActions';
import { switchMap, map, catchError, takeUntil, filter } from 'rxjs/operators';
import { concat, of, from, interval } from 'rxjs';
import { authClient } from '../api/auth/authClient';
import { AxiosError } from 'axios';
import { Notification, NOTIFICATION_TYPES, parseError, parseMisAuthError } from '../../utils/utils';
import { i18n } from '../translation/i18n';
import { RootState } from '../store.typings';
import { VALIDATE_USER_ACTION_STATUS_CODE } from '../../utils/recaptcha';

export const authorizeUserEpic: Epic<UserAction> = (action$) => {
    return action$.pipe(
        ofType(UserActionType.LOGIN_USER),
        switchMap((action) => {
            return concat(
                of(loginLoading()),
                from(authClient.simpleLogin(action.payload)).pipe(
                    map((respData) => {
                        return loginSuccess(respData);
                    }),
                    catchError((err: AxiosError) => {
                        if (err.response && err.response.status === VALIDATE_USER_ACTION_STATUS_CODE)
                            return of(validateUserAction(action.payload));
                        else
                            return of(
                                loginError(
                                    parseError(
                                        err,
                                        i18n.t('AuthScreen.errors.loginError', { errorText: err.response.statusText }),
                                    ),
                                ),
                            );
                    }),
                ),
            );
        }),
    );
};

export const sendMisUserInfoEpic: Epic<UserAction> = (action$) => {
    return action$.pipe(
        ofType(UserActionType.MIS_SEND_USER_INFO),
        switchMap((action) => {
            const { formData, uiType } = action.payload;

            return from(authClient.misSendUserInfo(formData, uiType)).pipe(
                map((respData) => {
                    if (respData.hasOwnProperty('userId')) {
                        return misAuthCompleteSuccessAction(respData);
                    }

                    return misUserInfoSuccess(respData);
                }),
                catchError((err: AxiosError) => of(loginError(parseMisAuthError(err)))),
            );
        }),
    );
};

const TIMEOUT_IN_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 10 * 1000;

export const checkMisAuthStatusEpic: Epic<UserAction, UserAction, RootState> = (action$, state$) => {
    const resetUserInfo$ = state$.pipe(filter((state) => !state.userReducer.misAuth.userInfo));

    return action$.pipe(
        ofType(UserActionType.MIS_USER_INFO_SUCCESS),
        switchMap(() => {
            return interval(CHECK_INTERVAL_MS).pipe(
                switchMap(() => {
                    const misAuth = state$.value.userReducer.misAuth;
                    if (Date.now() - misAuth.startTime > TIMEOUT_IN_MS) {
                        return of(misReset());
                    }
                    return from(authClient.misCheckAuthenticate()).pipe(
                        map((respData) => {
                            const notification: Notification = {
                                message:
                                    respData.authenticationStatus !== 'USER_AUTHENTICATED' &&
                                    respData.authenticationStatus !== 'OUTSTANDING_TRANSACTION'
                                        ? i18n.t(`AuthScreen.errors.misErrNotify.${respData.authenticationStatus}`)
                                        : null,
                                type: NOTIFICATION_TYPES[0],
                            };

                            if (
                                respData.authenticationStatus === 'USER_AUTHENTICATED' ||
                                respData.authenticationStatus === 'OUTSTANDING_TRANSACTION'
                            ) {
                                return misAuthDataLoad(respData);
                            }

                            if (respData.authenticationStatus === 'INTERNAL_ERROR' && respData.errorCode) {
                                notification.message = i18n.t(`AuthScreen.errors.misErrorCodes.${respData.errorCode}`);
                                misReset();
                            }

                            return misNotificationAction(notification);
                        }),
                        catchError((err: AxiosError) => of(loginError(parseMisAuthError(err)))),
                    );
                }),
                takeUntil(resetUserInfo$),
            );
        }),
    );
};

export const completeMisAuth: Epic<UserAction> = (action$) => {
    return action$.pipe(
        ofType(UserActionType.MIS_AUTH_COMPLETE_START),
        switchMap((action) => {
            const { taxNumber, uiType } = action.payload;

            return from(authClient.misAuthComplete(taxNumber, uiType)).pipe(
                map((respData) => {
                    return misAuthCompleteSuccessAction(respData);
                }),
                catchError((err: AxiosError) => of(loginError(parseMisAuthError(err)))),
            );
        }),
    );
};
